import { useTranslation } from "react-i18next";
import { TImage } from "../../../../../types";
import styles from "./card-trailer.module.less";
import { convertMetersToKm } from "../../../../../helpers/number-helper";

export type TTrailer = {
  id: number;
  title: string;
  maxLoad: string;
  totalWeight: string;
  measurements?: {
    length: string;
    width: string;
    height: string;
  };
  fromPrice?: number | string;
  image: TImage;
  stationName?: string;
  distanceInMeters?: number;
};

export type TCardTrailerProps = {
  trailer: TTrailer;
  onClick?: () => void;
  notAvailable?: boolean;
};

const CardTrailer = ({ trailer, onClick, notAvailable }: TCardTrailerProps) => {
  if (!trailer) {
    return null;
  }

  const { t } = useTranslation();

  const handleOnClick = () => {
    if (typeof onClick === "function") {
      onClick();
    }
  };

  return (
    <button
      className={[styles.wrapper, notAvailable && styles.notAvailable].join(" ")}
      onClick={notAvailable ? undefined : handleOnClick}
    >
      <div>
        {trailer.stationName && trailer.distanceInMeters && (
          <p className={[styles.nameAndDistance, "body-s"].join(" ")}>
            {convertMetersToKm(trailer.distanceInMeters)} km ({trailer.stationName})
          </p>
        )}
        <p className={[styles.title, "body-l bold"].join(" ")}>{trailer.title}</p>
        <div className={[styles.measurementsWrapper].join(" ")}>
          <div className={styles.measurementsItem}>
            <span className={[styles.measurementsItemLabel, "eyebrow"].join(" ")}>
              {t("TRAILER_RENTAL_TRAILER_MAX_LOAD")}
            </span>
            <span className={[styles.measurementsItemValue, "body-s"].join(" ")}>{trailer.maxLoad} kg</span>
          </div>
          <div className={styles.measurementsItem}>
            <span className={[styles.measurementsItemLabel, "eyebrow"].join(" ")}>
              {t("TRAILER_RENTAL_TRAILER_TOTAL_WEIGHT")}
            </span>
            <span className={[styles.measurementsItemValue, "body-s"].join(" ")}>{trailer.totalWeight} kg</span>
          </div>
          {(trailer?.measurements?.length || trailer.measurements?.width || trailer.measurements?.height) && (
            <div className={styles.measurementsItem}>
              <span className={[styles.measurementsItemLabel, "eyebrow"].join(" ")}>
                {t("TRAILER_RENTAL_TRAILER_MEASUREMENTS_LABEL")} {trailer.measurements?.length && <>L</>}{" "}
                {trailer?.measurements.width && <>&times; B</>} {trailer.measurements?.height && <> &times; H</>}
              </span>
              <span className={[styles.measurementsItemValue, "body-s"].join(" ")}>
                {trailer.measurements?.length && <>{trailer.measurements.length} </>}{" "}
                {trailer.measurements?.width && <>&times; {trailer.measurements.width} </>}{" "}
                {trailer.measurements?.height && <>&times; {trailer.measurements.height}</>} cm
              </span>
            </div>
          )}
        </div>
      </div>
      <div>
        <div className={styles.imageWrapper}>
          {trailer.fromPrice && (
            <div className={styles.chip}>
              <span className={[styles.chipLabel, "body-s"].join(" ")}>
                {t("TRAILER_RENTAL_TRAILER_FROM_PRICE_LABEL", { price: trailer.fromPrice })}
              </span>
            </div>
          )}
          <img className={styles.trailerImage} src={trailer.image.url} alt={trailer.image.alt} />
        </div>
      </div>
      {notAvailable && (
        <div className={styles.notAvailableCover}>
          <p className={[styles.notAvailableMessage, "body-l bold"].join(" ")}>
            {t("TRAILER_RENTAL_TRAILER_NOT_AVAILABLE")}
          </p>
        </div>
      )}
    </button>
  );
};

export default CardTrailer;
